import { FormEvent, ReactNode, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { useIsSentinelUser } from '../../../authUserProvider'
import s from './styles.module.scss'

interface EsignDetail {
    linkSent: boolean
    sendCount: number
    lastSentDatetime: string | null
    sendType: string | null
    recipient: string | null
    senderAgent: string
    senderAgentEmail: string
    senderTeam: string
    linkOpened: boolean
    signed: boolean
    signedStatus: string
    signedDatetime: string | null
    ipAddress: string
    browser: string
    device: string
    caseRef: string
}

interface ClientResult {
    id: number
    title: string
    firstname: string
    lastname: string
    email: string
    telephone: string
    dob: string
    address: string
    postcode: string
    createdAt: string | null
    esign: EsignDetail
}

const fmtDateTime = (value?: string | null) => {
    if (!value) return '—'
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const fmtDate = (value?: string | null) => {
    if (!value) return '—'
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const orDash = (value?: string | null) => (value && String(value).trim() ? value : '—')

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
    <div className={s.field}>
        <span className={s.fieldLabel}>{label}</span>
        <span className={s.fieldValue}>{value}</span>
    </div>
)

const ClientCard = ({ client }: { client: ClientResult }) => {
    const e = client.esign
    const fullName = [client.title, client.firstname, client.lastname].filter(Boolean).join(' ') || 'Unnamed client'

    return (
        <div className={s.card}>
            <div className={s.cardHeader}>
                <h4 className={s.cardName}>
                    {fullName} <span className={s.clientId}>#{client.id}</span>
                </h4>
                <div className={s.badges}>
                    {e.signed ? (
                        <span className={`${s.badge} ${s.badgeSigned}`}>Signed</span>
                    ) : e.linkSent ? (
                        <span className={`${s.badge} ${s.badgeSent}`}>Link sent</span>
                    ) : (
                        <span className={`${s.badge} ${s.badgeNone}`}>Not sent</span>
                    )}
                    {e.linkOpened && !e.signed && <span className={`${s.badge} ${s.badgeOpened}`}>Link opened</span>}
                </div>
            </div>

            <div className={`${s.section} ${s.sectionClient}`}>
                <div className={s.sectionTitle}>Client details</div>
                <div className={s.grid}>
                    <Field label="Email" value={orDash(client.email)} />
                    <Field label="Phone" value={orDash(client.telephone)} />
                    <Field label="Date of birth" value={fmtDate(client.dob)} />
                    <Field label="Address" value={orDash(client.address)} />
                    <Field label="Lead created" value={fmtDate(client.createdAt)} />
                    <Field label="Case ref" value={orDash(e.caseRef)} />
                </div>
            </div>

            <div className={`${s.section} ${s.sectionEsign}`}>
                <div className={s.sectionTitle}>eSign link</div>
                <div className={s.grid}>
                    <Field label="Team" value={orDash(e.senderTeam)} />
                    <Field
                        label="Agent"
                        value={
                            e.senderAgent ? (
                                <>
                                    {e.senderAgent}
                                    {e.senderAgentEmail ? <span className={s.subtle}> ({e.senderAgentEmail})</span> : null}
                                </>
                            ) : (
                                '—'
                            )
                        }
                    />
                    <Field label="Sent" value={e.linkSent ? 'Yes' : 'No'} />
                    <Field label="Channel" value={orDash(e.sendType ? e.sendType.toUpperCase() : null)} />
                    <Field label="Recipient" value={orDash(e.recipient)} />
                    <Field label="Date/time sent" value={fmtDateTime(e.lastSentDatetime)} />
                    <Field label="Times sent" value={e.sendCount} />
                    <Field label="Link opened" value={e.linkOpened ? 'Yes' : 'No'} />
                </div>
            </div>

            {e.signed && (
                <div className={`${s.section} ${s.sectionSignature}`}>
                    <div className={s.sectionTitle}>Signature</div>
                    <div className={s.grid}>
                        <Field label="Status" value={orDash(e.signedStatus)} />
                        <Field label="Date/time signed" value={fmtDateTime(e.signedDatetime)} />
                        <Field label="IP address" value={orDash(e.ipAddress)} />
                        <Field label="Browser" value={orDash(e.browser)} />
                        <Field label="Device" value={orDash(e.device)} />
                    </div>
                </div>
            )}
        </div>
    )
}

const PcpReferralClientSearchImpl = () => {
    const [term, setTerm] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [results, setResults] = useState<ClientResult[] | null>(null)

    const runSearch = async (e?: FormEvent) => {
        e?.preventDefault()
        const q = term.trim()
        if (!q) {
            setError('Enter an email, name or phone number to search.')
            return
        }
        setLoading(true)
        setError(null)
        try {
            const res = await api({ url: '/pcp-referral-admin-client-search', data: { searchTerm: q } })
            if (res.data?.success) {
                setResults((res.data.results as ClientResult[]) || [])
            } else {
                setError(res.data?.message || 'Search failed')
                setResults(null)
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Search failed')
            setResults(null)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={s.statGroup}>
            <h3 className={s.statGroupTitle}>Client Search</h3>
            <p className={s.subtitle}>
                Search a PCP Referral client by email, name or phone number to see their details, eSign status, the
                team &amp; agent who sent the link, and — if signed — the IP, browser, device and timestamps.
            </p>

            <form className={s.searchRow} onSubmit={runSearch}>
                <input
                    className={s.searchInput}
                    type="text"
                    placeholder="Email, name or phone number…"
                    value={term}
                    onChange={(ev) => setTerm(ev.target.value)}
                    disabled={loading}
                />
                <button className={s.searchButton} type="submit" disabled={loading}>
                    {loading ? 'Searching…' : 'Search'}
                </button>
            </form>

            {loading && (
                <div className={s.loading}>
                    <span className={s.spinner} />
                    Searching…
                </div>
            )}

            {!loading && error && (
                <div className={s.error}>
                    <span>{error}</span>
                </div>
            )}

            {!loading && !error && results && results.length === 0 && (
                <div className={s.empty}>No clients matched that search.</div>
            )}

            {!loading && !error && results && results.length > 0 && (
                <>
                    <div className={s.resultCount}>
                        {results.length} {results.length === 1 ? 'result' : 'results'}
                        {results.length >= 50 ? ' (showing first 50 — refine your search)' : ''}
                    </div>
                    <div className={s.results}>
                        {results.map((client) => (
                            <ClientCard key={client.id} client={client} />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

// Sentinel-admin only. Rendered inside the admin visibility group on the PCP
// Referral overview, but self-gates too so it can never leak the PII-heavy
// search to a non-Sentinel user (the backend independently enforces this).
export const PcpReferralClientSearch = () => {
    const isSentinelUser = useIsSentinelUser()
    if (!isSentinelUser) return null
    return <PcpReferralClientSearchImpl />
}

export default PcpReferralClientSearch
